import { Typography, Tooltip, Dropdown, Tag } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import {
  BTN_TXT,
  GLOBAL_CONSTANTS,
  STATUS,
  BUS_STATUS_DISPLAY_NAMES,
  DEAL_CATEGORY_COLUMN_FILTERS
} from "../../../constants/constants";
import { formatCurrency } from "../../../helpers/helpers";
import { safeStringSort, safeNumberSort } from "../../../helpers/sortHelpers";
import { PERM_CODE } from "../../../constants/PermissionCodes";

const { Text } = Typography;

export const getBusColumns = ({
  status,
  onDeactivate,
  onDeliquent,
  onBlacklist,
  onActivate,
  onView,
  onViewEnterprises,
  privileges = [] } = {}) => {
  const columns = [
    {
      title: "Business Name",
      dataIndex: "busName",
      key: "busName",
      // width: 250,
      sorter: (a, b) => safeStringSort(a?.busName, b?.busName) },
    {
      title: "Deal Category",
      dataIndex: "dealCategoryName",
      key: "dealCategoryName",
      // width: 150,
      filters: DEAL_CATEGORY_COLUMN_FILTERS,
      onFilter: (value, record) => record?.dealCategoryName === value,
      sorter: (a, b) => safeStringSort(a?.dealCategoryName, b?.dealCategoryName),
      render: (text, record) => {
        const categoryName = text || record?.dealCategoryName;

        if (!categoryName) {
          return "N/A";
        }

        const normalized = categoryName.toLowerCase();

        if (normalized.includes("invoice")) {
          return <Tag color="blue">{categoryName}</Tag>;
        }

        if (normalized.includes("supply chain")) {
          return <Tag color="purple">{categoryName}</Tag>;
        }

        if (normalized.includes("joint venture")) {
          return <Tag color="green">{categoryName}</Tag>;
        }

        return <Tag color="default">{categoryName}</Tag>;
      } },
    {
      title: "Business",
      dataIndex: "businessTypeName",
      key: "businessTypeName",
      // width: 150,
      sorter: (a, b) => safeStringSort(a?.businessTypeName, b?.businessTypeName) },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      sorter: (a, b) => {
        const statusA = a?.statusName || BUS_STATUS_DISPLAY_NAMES?.[a?.statusId] || "";
        const statusB = b?.statusName || BUS_STATUS_DISPLAY_NAMES?.[b?.statusId] || "";
        return safeStringSort(statusA, statusB);
      },
      render: (_, record) => {
        const statusText =
          record?.statusName ||
          BUS_STATUS_DISPLAY_NAMES?.[record?.statusId] ||
          record?.statusId ||
          GLOBAL_CONSTANTS.NA;
        return <Tag color="blue">{statusText}</Tag>;
      } },
    {
      title: "Industry",
      dataIndex: "industryTypeName",
      key: "industryTypeName",
      width: 150,
      sorter: (a, b) => safeStringSort(a?.industryTypeName, b?.industryTypeName) },
    {
      title: "Total Limit",
      dataIndex: "totalCreditLimit",
      key: "totalCreditLimit",
      width: 150,
      render: (text) => (text ? formatCurrency(text) : GLOBAL_CONSTANTS.NA),
      sorter: (a, b) => safeNumberSort(a?.totalCreditLimit, b?.totalCreditLimit) },
    {
      title: "Utilized",
      dataIndex: "creditUtilizationPercentage",
      key: "creditUtilizationPercentage",
      width: 200,
      sorter: (a, b) => safeNumberSort(a?.creditUtilizationPercentage, b?.creditUtilizationPercentage),

      render: (text) => {
        if (text === null || text === undefined || text === "") {
          return GLOBAL_CONSTANTS.NA;
        }
        const pct = Number(text) * 100;
        if (Number.isNaN(pct)) {
          return GLOBAL_CONSTANTS.NA;
        }
        return `${parseFloat(pct.toFixed(2))}%`;
      } },
    {
      title: "Top Enterprise Name",
      dataIndex: "topBusEntName",
      key: "topBusEntName",
      width: 250,
      sorter: (a, b) => safeStringSort(a?.topBusEntName, b?.topBusEntName),

      render: (text, record) =>
        text ? (
          <Tooltip title={text}>
            <span
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                // color: "#1677ff",
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis" }}
              onClick={() => onViewEnterprises(record)}
            >
              {text} ({record?.entList?.length || 0})
            </span>
          </Tooltip>
        ) : (
          GLOBAL_CONSTANTS.NA
        ) },
    {
      title: "Comments",
      dataIndex: "remarks",
      key: "remarks",
      width: 150,
      sorter: (a, b) => safeStringSort(a?.remarks, b?.remarks),

      render: (text) =>
        text ? (
          <Tooltip title={text}>
            <Text ellipsis style={{ maxWidth: 120, display: "block" }}>
              {text}
            </Text>
          </Tooltip>
        ) : (
          GLOBAL_CONSTANTS.NA
        ) },
  ];

  columns.push({
    title: "Actions",
    key: "actions",
    fixed: "right",
    render: (_, record) => {
      const rowStatus =
        record?.statusId ||
        (Array.isArray(status) ? (status.length === 1 ? status[0] : null) : status);
      const items = [];

      // 👁 View (always visible)
      items.push({
        key: "view",
        label: "View",
        onClick: () => onView?.(record) });

      // 🟢 ACTIVE
      if (rowStatus === STATUS.ACTIVE.ID) {
        if (privileges.includes(PERM_CODE.BASDE)) {
          items.push({
            key: "deactivate",
            label: BTN_TXT.DEACTIVATE,
            onClick: () => onDeactivate?.(record) });
        }

        if (privileges.includes(PERM_CODE.BASDL)) {
          items.push({
            key: "delinquent",
            label: BTN_TXT.DELINQUENT,
            onClick: () => onDeliquent?.(record) });
        }

        if (privileges.includes(PERM_CODE.BASBL)) {
          items.push({
            key: "blacklist",
            label: BTN_TXT.BLACKLIST,
            onClick: () => onBlacklist?.(record) });
        }
      }

      // 🔴 DEACTIVATED → Reactivate
      if (
        rowStatus === STATUS.DEACTIVATED.ID &&
        privileges.includes(PERM_CODE.BDSRE)
      ) {
        items.push({
          key: "reactivate",
          label: BTN_TXT.REACTIVATE,
          onClick: () => onActivate?.(record) });
      }

      // 🟡 DELINQUENT → Reactivate
      if (
        rowStatus === STATUS.DELINQUENT.ID &&
        privileges.includes(PERM_CODE.BLSRE)
      ) {
        items.push({
          key: "reactivate",
          label: BTN_TXT.REACTIVATE,
          onClick: () => onActivate?.(record) });
      }

      return (
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <MoreOutlined
            style={{ fontSize: "18px", cursor: "pointer" }}
          />
        </Dropdown>
      );
    } });

  return columns;
};
